import React, { useState } from 'react';
import * as Styled from './DSOTableHeader.styles';
import { TableHeaderProps } from './DSOTable.types';

/**
 * TableHeader Component
 *
 * A reusable header component for DSO tables that provides a consistent
 * interface with configurable title and search functionality. This component
 * is designed to be used with DSOTable and can be adapted for various use cases
 * including Order History, Product Catalogs, and other data tables.
 *
 * Features:
 * - Configurable title
 * - Real-time search with debounced callback
 * - Responsive layout with clear visual hierarchy
 * - Accessible search input with icon
 *
 * @param {string} [title='Table'] - Display title for the table
 * @param {function} [onSearch] - Callback function triggered when search term changes
 * @param {string} [searchPlaceholder='Search...'] - Placeholder text for search input
 *
 * @example
 * // Order History usage
 * <TableHeader
 *   title="Order History"
 *   onSearch={handleSearch}
 *   searchPlaceholder="Search orders..."
 * />
 *
 * @example
 * // Product Catalog usage
 * <TableHeader
 *   title="Product Catalog"
 *   onSearch={handleProductSearch}
 *   searchPlaceholder="Search products..."
 * />
 *
 * @example
 * // Simple usage with defaults
 * <TableHeader onSearch={handleSearch} />
 */
const TableHeader: React.FC<TableHeaderProps> = ({
  title = 'Table',
  onSearch,
  searchPlaceholder = 'Search...',
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (onSearch) {
      onSearch(value);
    }
  };

  return (
    <Styled.HeaderContainer>
      <Styled.LeftSection>
        <Styled.Title>{title}</Styled.Title>
      </Styled.LeftSection>

      <Styled.RightSection>
        <Styled.SearchBarContainer>
          <Styled.SearchIcon>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              width="20"
              height="20"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
              />
            </svg>
          </Styled.SearchIcon>
          <Styled.SearchInput
            type="text"
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </Styled.SearchBarContainer>
      </Styled.RightSection>
    </Styled.HeaderContainer>
  );
};

export default TableHeader;
